import React, { useMemo, useEffect, useState, useCallback } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

// Dial codes and national number length ranges
const DIAL = { in:"91", us:"1", ae:"971", gb:"44", cn:"86", sg:"65", au:"61", ca:"1" };
const NSN_LEN = {
  in: { min: 10, max: 10 },
  us: { min: 10, max: 10 },
  ca: { min: 10, max: 10 },
  ae: { min: 9,  max: 9  },
  gb: { min: 9,  max: 10 },
  cn: { min: 11, max: 11 },
  sg: { min: 8,  max: 8  },
  au: { min: 9,  max: 9  },
};
const FALLBACK_LEN = { min: 6, max: 15 };

const digitsOnly = (v) => (v == null ? "" : String(v).replace(/[^\d]/g, ""));

function inferCountry(fullDigits, allowed = []) {
  if (!fullDigits) return null;
  let best = null, bestLen = 0;
  for (const c of allowed) {
    const cc = c === "uk" ? "gb" : c;
    const code = DIAL[cc];
    if (code && fullDigits.startsWith(code) && code.length > bestLen) {
      best = cc; bestLen = code.length;
    }
  }
  return best;
}

export default function ControlledPhoneInput({
  value = "", onChange,
  country = "in",
  onlyCountries = ["in","us","ae","gb","cn","sg","au","ca"],
  label = "Phone", disabled = false, placeholder="Enter Number", ...rest
}) {
  const [errorText, setErrorText] = useState('');
  const normalizedOnly = useMemo(
    () => onlyCountries.map((c) => (c === "uk" ? "gb" : c)),
    [onlyCountries]
  );
  const defaultCountry = country === "uk" ? "gb" : country;

  // 1) State first
  // const [internal, setInternal] = useState(() => digitsOnly(value)); // full digits incl. dial code
  // const [cc, setCc] = useState(() =>
  //   inferCountry(digitsOnly(value), normalizedOnly) || defaultCountry
  // );

  const [internal, setInternal] = useState(() => digitsOnly(value) || "");
const [cc, setCc] = useState(() =>
  inferCountry(digitsOnly(value), normalizedOnly) || defaultCountry
);

  // 2) Effects that may use cc AFTER it's declared
  // useEffect(() => {
  //   const digits = digitsOnly(value);
  //   if (digits === internal) return; // don't clobber user's edits
  //   setInternal(digits);
  //   setCc(inferCountry(digits, normalizedOnly) || defaultCountry);
  // }, [value, normalizedOnly, defaultCountry, internal]);
  useEffect(() => {
  const digits = digitsOnly(value);

  // If form reset (value is null/empty), restore default country
  if (!digits) {
    setInternal("");
    setCc(defaultCountry);
    return;
  }

  if (digits === internal) return;
  setInternal(digits);
  setCc(inferCountry(digits, normalizedOnly) || defaultCountry);
}, [value, normalizedOnly, defaultCountry]);

  // 3) Memos/callbacks that read cc AFTER it exists
  const lenRange = useMemo(() => NSN_LEN[cc] || FALLBACK_LEN, [cc]);

  const validatePhone = useCallback((val, countryData) => {
    const digits = digitsOnly(val);
    const dial = String(countryData?.dialCode || "");
    const national = digits.startsWith(dial) ? digits.slice(dial.length) : digits;
    const { min, max } = NSN_LEN[countryData?.countryCode] || FALLBACK_LEN;
    if (national.length === 0) return true; // allow empty while typing
   //return (national.length >= min && national.length <= max)? true : `Enter ${min}-${max} digits`;
    if (national.length === 0) { setErrorText(''); return true; }

  if (national.length >= min && national.length <= max) {
    setErrorText('');
    return true;
  }
  const msg = `Enter ${min}-${max} digits`;
  setErrorText(msg);
  return false; // return false so library marks input invalid
    //setErrorText(msg);
  }, []);

  const handleChange = useCallback((val, data) => {
    // Optional: hard-cap national length while typing
    const dial = String(data?.dialCode || "");
    let digits = digitsOnly(val);
    let national = digits.startsWith(dial) ? digits.slice(dial.length) : digits;
    const { max } = NSN_LEN[data?.countryCode] || FALLBACK_LEN;
    if (national.length > max) {
      national = national.slice(0, max);
      digits = dial + national;
    }

    setInternal(digits);
    if (data?.countryCode) setCc(data.countryCode);
    onChange?.(digits, data, digits ? `+${digits}` : "");
  }, [onChange]);

  return (
    <div className="w-full">
      {label && <label className="text-xs font-regular mb-0.5 text-gray-700 flex items-center gap-1">{label}</label>}
      <PhoneInput
        country={cc || defaultCountry}
        containerClass="phone-field"
        onlyCountries={normalizedOnly}
        value={internal || DIAL[cc || defaultCountry] || ""}
        onChange={handleChange}
        separateDialCode
        countryCodeEditable={false}
        enableSearch
        specialLabel=""
        isValid={validatePhone}
        inputProps={{
          name: "phone",
          minLength: lenRange.min,
          pattern: `\\d{${lenRange.min},${lenRange.max}}`,
        }}
        disabled={disabled}
        placeholder={placeholder}
        containerStyle={{ width: "100%" }}
        inputStyle={{
          width: "100%", height: 40, fontSize: 14,
          border: "0px solid #d1d5db", borderRadius: 6,
          background: disabled ? "#f9fafb" : "#f3f4f6",
          color: disabled ? "#6b7280" : "#111827",
          
        }}
        buttonStyle={{
          background: disabled ? "#f9fafb" : "#f3f4f6", 
          border: "0px solid #d1d5db", borderRight: "none",
          borderRadius: "6px 0 0 6px",
          padding: "0 8px",
        }}
        {...rest}
      />
      {/* your own line below */}
{errorText ? (
  <p className="mt-1 text-xs text-red-600">{errorText}</p>
) : null}
    </div>
  );
}
